/**
 * CustomerAutocomplete — input con autocomplete sobre clientes existentes
 * + opción para crear uno nuevo inline.
 *
 * Forma del valor (controlado por el padre): `CustomerSelection`:
 *   - `{ kind: 'existing', customerId, preview }` — selección de uno existente
 *   - `{ kind: 'new', name, email, phone? }` — datos del mini-form embebido
 *
 * El padre decide qué hacer con `value` al construir el body del POST:
 * si `kind === 'existing'` envía `customerId`; si `kind === 'new'` envía
 * el sub-objeto `customer` con `{ name, email, phone? }`.
 */
import { useEffect, useRef, useState } from 'react';
import * as Icons from '@components/Icons';
import { adminCustomersSearchApi } from '@api/client';
import type { Customer } from '@api/types';

export type CustomerSelection =
  | {
      kind: 'existing';
      customerId: string;
      preview: { name: string; email: string };
    }
  | {
      kind: 'new';
      name: string;
      email: string;
      phone?: string;
    };

interface Props {
  /** Scope opcional para la búsqueda. Solo lo respeta el backend si el
   *  caller es superadmin; staff/center_admin lo tienen forzado server-side. */
  centerId?: string;
  value: CustomerSelection | null;
  onChange: (next: CustomerSelection | null) => void;
}

/** Debounce ms para la búsqueda en backend. */
const DEBOUNCE_MS = 250;

export function CustomerAutocomplete({ centerId, value, onChange }: Props) {
  // El input del autocomplete refleja:
  //   - lo que el usuario escribe (`query`) cuando está editando.
  //   - el preview del cliente seleccionado cuando `value.kind === 'existing'`.
  //   - vacio cuando `value.kind === 'new'` (mostramos un summary aparte).
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);

  // Mini-form embebido "Crear cliente nuevo".
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newErr, setNewErr] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(query.trim()); }, DEBOUNCE_MS);
    return () => { clearTimeout(t); };
  }, [query]);

  // Búsqueda con guard de race conditions
  useEffect(() => {
    if (!open) return;
    if (debounced.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminCustomersSearchApi
      .search(debounced, centerId, 10)
      .then((resp) => {
        if (cancelled) return;
        setResults(resp.items);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debounced, centerId, open]);

  // Cierra el dropdown al click-outside
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('mousedown', onClick); };
  }, []);

  function pickExisting(c: Customer) {
    onChange({
      kind: 'existing',
      customerId: c.id,
      preview: { name: c.name, email: c.email },
    });
    setQuery('');
    setOpen(false);
    setShowNewForm(false);
  }

  function startNewForm() {
    setShowNewForm(true);
    setOpen(false);
    // Pre-fill el nombre con lo que ya tecleaba el usuario, si parece un nombre.
    const looksLikeEmail = query.includes('@');
    if (looksLikeEmail) setNewEmail(query.trim());
    else setNewName(query.trim());
  }

  function confirmNewForm() {
    setNewErr(null);
    const name = newName.trim();
    const email = newEmail.trim();
    const phone = newPhone.trim();
    if (!name) { setNewErr('El nombre es obligatorio.'); return; }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewErr('Email no válido.'); return;
    }
    onChange({
      kind: 'new',
      name,
      email,
      ...(phone ? { phone } : {}),
    });
    setShowNewForm(false);
    setQuery('');
  }

  function clearSelection() {
    onChange(null);
    setQuery('');
    setShowNewForm(false);
    setNewName(''); setNewEmail(''); setNewPhone(''); setNewErr(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  // Render del estado "seleccionado existente" — chip + botón limpiar
  if (value?.kind === 'existing') {
    return (
      <div className="cust-ac-selected">
        <div className="cust-ac-selected-info">
          <div className="cust-ac-selected-name">{value.preview.name}</div>
          <div className="cust-ac-selected-meta">{value.preview.email}</div>
        </div>
        <button
          type="button"
          className="icon-btn"
          data-variant="ghost"
          onClick={clearSelection}
          aria-label="Cambiar cliente"
          title="Cambiar cliente"
        >
          <Icons.X size={14} />
        </button>
      </div>
    );
  }

  // Render del estado "nuevo cliente — datos ya capturados"
  if (value) {
    return (
      <div className="cust-ac-selected" data-variant="new">
        <div className="cust-ac-selected-info">
          <div className="cust-ac-selected-name">
            {value.name} <span className="pill" data-tone="warn">Nuevo</span>
          </div>
          <div className="cust-ac-selected-meta">
            {value.email}{value.phone ? ` · ${value.phone}` : ''}
          </div>
        </div>
        <button
          type="button"
          className="icon-btn"
          data-variant="ghost"
          onClick={clearSelection}
          aria-label="Editar datos"
          title="Editar datos"
        >
          <Icons.X size={14} />
        </button>
      </div>
    );
  }

  // Render del estado "mini-form nuevo cliente"
  if (showNewForm) {
    return (
      <div className="cust-ac-new-form">
        <div className="field">
          <label className="field-label">Nombre</label>
          <input
            className="input"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); }}
            autoFocus
          />
        </div>
        <div className="field">
          <label className="field-label">Email</label>
          <input
            className="input"
            type="email"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value); }}
          />
        </div>
        <div className="field">
          <label className="field-label">Teléfono <span className="soft">(opcional)</span></label>
          <input
            className="input"
            value={newPhone}
            onChange={(e) => { setNewPhone(e.target.value); }}
            placeholder="+34 …"
          />
        </div>
        {newErr && <div className="cust-ac-err">{newErr}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn"
            data-size="sm"
            onClick={() => { setShowNewForm(false); }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn"
            data-variant="primary"
            data-size="sm"
            onClick={confirmNewForm}
          >
            <Icons.Check size={12} /> Usar estos datos
          </button>
        </div>
      </div>
    );
  }

  // Render del estado "buscador"
  return (
    <div className="cust-ac" ref={containerRef}>
      <div className="search">
        <Icons.Search size={14} className="ico" />
        <input
          ref={inputRef}
          className="input"
          placeholder="Buscar cliente por nombre, email o teléfono…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); }}
        />
      </div>

      {open && (
        <div className="cust-ac-dropdown" role="listbox">
          {loading && (
            <div className="cust-ac-empty">Buscando…</div>
          )}
          {!loading && debounced.length < 2 && (
            <div className="cust-ac-empty">Escribe al menos 2 caracteres.</div>
          )}
          {!loading && debounced.length >= 2 && results.length === 0 && (
            <div className="cust-ac-empty">Sin coincidencias.</div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              className="cust-ac-row"
              onClick={() => { pickExisting(c); }}
              role="option"
            >
              <div>
                <div className="cust-ac-row-name">{c.name}</div>
                <div className="cust-ac-row-meta">
                  {c.email}{c.phone ? ` · ${c.phone}` : ''}
                </div>
              </div>
            </button>
          ))}
          <button
            type="button"
            className="cust-ac-row cust-ac-row-create"
            onClick={startNewForm}
          >
            <Icons.Plus size={14} />
            <span>Crear cliente nuevo{query.trim() ? ` con “${query.trim()}”` : ''}</span>
          </button>
        </div>
      )}
    </div>
  );
}
